import { createSelector } from 'reselect'
import type { OfferPageRoom, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import type { SetRequired } from 'type-fest'
import { arrayToObject, EmptyArray, min, partitionBy, sortBy } from 'lib/array/arrayUtils'
import { deepFreeze, EmptyObject, omitKeys } from 'lib/object/objectUtils'
import { aggregateBedbankMultiRoomPrices, combineBedbankRates } from 'lib/hotels/bedbankUtils'
import type { AggregatedMultiRoomPrices } from 'lib/hotels/bedbankUtils'

const initialRooms: Array<OfferPageRoom> = []

const getOfferRates = (state: OfferPageState, appState: App.State) => appState.offer.bedbankOfferRates[state.bedbankOffer?.id ?? ''] ?? EmptyObject

const roomHasRoomTypeSelection = (room: OfferPageRoom): room is SetRequired<OfferPageRoom, 'roomId'> => !!room.roomId

function determineEffectiveRatesPerRoom(
  offerRates: { [rateKey: string]: Array<App.BedbankRate> },
  rooms: Array<OfferPageRoom> = initialRooms,
  checkIn: string | undefined,
  checkOut: string | undefined,
  isFlightsSelected: boolean,
): Record<string, Array<App.BedbankRate>> {
  return arrayToObject(
    rooms,
    room => room.id,
    room => {
      if (!checkIn || !checkOut) { return EmptyArray }
      const rates = offerRates[getBedbankRateKey([room], checkIn, checkOut)] ?? EmptyArray
      const [bundleRates, hotelOnlyRates] = partitionBy(rates, rate => rate.isFlightBundle)
      // Recombine to get the final set of rates, with matching bundle and non-bundle rates resolved to one rate based on whether flights are selected
      return combineBedbankRates(hotelOnlyRates, bundleRates, isFlightsSelected)
    },
  )
}

const getEffectiveRatesPerRoom = createSelector(
  (state: OfferPageState, appState: App.State) => getOfferRates(state, appState),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState) => state.flights,
  determineEffectiveRatesPerRoom,
)

const getHotelOnlyRatesPerRoom = createSelector(
  (state: OfferPageState, appState: App.State) => getOfferRates(state, appState),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  () => false,
  determineEffectiveRatesPerRoom,
)

const getBundleRatesPerRoom = createSelector(
  (state: OfferPageState, appState: App.State) => getOfferRates(state, appState),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  () => true,
  determineEffectiveRatesPerRoom,
)

export const getTotalAvailabilityByPackageAndSupplier = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState, appState: App.State) => getEffectiveRatesPerRoom(state, appState),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (offer, offerRatesByRoom, rooms = initialRooms, checkIn, checkOut) => {
    if (!offer || !checkIn || !checkOut) {
      return {}
    }

    // Gets the available rooms count of each package (i.e. room type), per supplier code.
    // It's assumed that for all rates of a particular room type and supplier,
    // they will have the same availability.
    const availabilityByPackageAndSupplier: Record<string, Record<string, number>> = {}
    for (const room of rooms) {
      const rates = offerRatesByRoom[room.id]
      for (const rate of rates) {
        if (typeof rate.availableRooms === 'number') {
          availabilityByPackageAndSupplier[rate.roomId] = availabilityByPackageAndSupplier[rate.roomId] ?? {}
          availabilityByPackageAndSupplier[rate.roomId][rate.supplierCode] = rate.availableRooms
        }
      }
    }

    // Since some other selectors will work with a copy of this object and mutate the copies,
    // freeze this so nobody accidentally mutates the original.
    deepFreeze(availabilityByPackageAndSupplier)

    return availabilityByPackageAndSupplier
  },
)

export const getOverallAvailabilityByPackage = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState, appState: App.State) => getEffectiveRatesPerRoom(state, appState),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (offer, offerRatesByRoom, rooms = initialRooms, checkIn, checkOut) => {
    if (!offer || !checkIn || !checkOut) {
      return {}
    }

    // Gets the available rooms count of each package (i.e. room type).
    // Different suppliers can have different availability, and we don't know if those availabilities
    // refer to the same actual inventory. Therefore we shouldn't let the user book more rooms than
    // the highest inventory count from any one supplier.
    const overallAvailabilityByPackage: Record<string, number> = {}
    for (const room of rooms) {
      const rates = offerRatesByRoom[room.id]
      for (const rate of rates) {
        if (typeof rate.availableRooms === 'number') {
          // Take the largest availability from all suppliers
          overallAvailabilityByPackage[rate.roomId] = Math.max((overallAvailabilityByPackage[rate.roomId] ?? 0), rate.availableRooms)
        }
      }
    }

    // Since some other selectors will work with a copy of this object and mutate the copies,
    // freeze this so nobody accidentally mutates the original.
    Object.freeze(overallAvailabilityByPackage)

    return overallAvailabilityByPackage
  },
)

interface Selection {
  package: App.BedbankPackage
  rate?: App.BedbankRate
  selectionType: 'alreadySelected' | 'wasBlank' | 'wasUnavailable'
}

function determinePackagesByRoom(
  offer: App.BedbankOffer | undefined,
  offerRatesByRoom: Record<string, Array<App.BedbankRate>>,
  rooms: Array<OfferPageRoom> = initialRooms,
  checkIn: string | undefined,
  checkOut: string | undefined,
  availabilityByPackageAndSupplier: Record<string, Record<string, number>>,
  overallAvailabilityByPackage: Record<string, number>,
  selectedBedbankRoomTypeId: string | undefined,
): Record<string, Selection | undefined> {
  if (!offer) {
    return {}
  }

  const packagesById = arrayToObject(offer.packages, pkg => pkg.roomId)

  // We want to process the rooms in descending order of occupant count, to ensure rooms with fewer people
  // aren't hogging larger room types that are required by rooms with more people.
  // Among rooms with the same occupant count, handle ones with pre-existing selections first in order to
  // preserve those selections.
  let roomsToProcess = sortBy(
    rooms,
    room => room.roomId ? 1 : -1,
    'desc',
  )
  roomsToProcess = sortBy(
    rooms,
    room => room.adults + (room.children ?? 0) + (room.infants ?? 0),
    'desc',
  )

  if (!checkIn || !checkOut) {
    // No dates? No problem! Just assign the cheapest cheapest package to any rooms that don't already have a selection
    const cheapestPackage = min(offer.packages, pkg => pkg.sell?.price ?? 0)
    if (!cheapestPackage) { return {} }

    const packageIds = new Set(offer.packages.map(pkg => pkg.roomId))

    return arrayToObject(
      rooms,
      room => room.id,
      (room): Selection => {
        const hasValidRoomIdAlready = packageIds.has(room.roomId ?? '')
        if (hasValidRoomIdAlready) {
          return { package: packagesById[room.roomId!], selectionType: 'alreadySelected' }
        } else {
          return { package: cheapestPackage, selectionType: room.roomId ? 'wasUnavailable' : 'wasBlank' }
        }
      },
    )
  }

  // Important: Spread these to new objects since we'll be mutating them
  const remainingRoomsByPackageAndSupplier = structuredClone(availabilityByPackageAndSupplier)
  const remainingOverallAvailabilityByPackage = { ...overallAvailabilityByPackage }

  // Store the room type and rate allocated to each room
  const packageSelectionsByRoom: Record<string, Selection | undefined> = {}

  roomsToProcess
    .forEach(room => {
      let roomAllocated = false
      const rates = offerRatesByRoom[room.id]
      if (roomHasRoomTypeSelection(room)) {
        // First of all, we check for rooms that have a package selected
        const selectedPackage = packagesById[room.roomId]
        const isPackageStillAvailable = !!(selectedPackage && remainingOverallAvailabilityByPackage[room.roomId] > 0)

        let selectedRate = rates.find(rate => rate.id === room.rateId && rate.roomId === selectedPackage?.roomId)

        // If the room already has a package selected,
        // and it still with capacity valid and hotel available,
        // and with available rooms, then it still a valid selection.
        // So we store this selection into active packages and
        // discount one available room for this package
        if (isPackageStillAvailable) {
          if (!selectedRate || !remainingRoomsByPackageAndSupplier[room.roomId]?.[selectedRate.supplierCode]) {
            // Room is available but rate isn't - choose the cheapest available rate
            const applicableRates = rates.filter(rate => rate.roomId === selectedPackage.roomId && remainingRoomsByPackageAndSupplier[room.roomId]?.[rate.supplierCode])

            selectedRate = min(applicableRates, rate => rate.totals.total)
          }

          if (selectedRate) {
            const selection: Selection = { package: selectedPackage, rate: selectedRate, selectionType: 'alreadySelected' }

            packageSelectionsByRoom[room.id] = selection
            remainingOverallAvailabilityByPackage[selection.package.roomId] -= 1
            remainingRoomsByPackageAndSupplier[selection.package.roomId][selectedRate.supplierCode] -= 1
            roomAllocated = true
          }
        }
      }

      if (!roomAllocated) {
        // If the room doesn't have any package selected, or its
        // selection becomes invalid (see above), then we will choose
        // the cheapest package available for this room (if any).
        // But if there's a `selectedBedbankRoomTypeId`, select the cheapest rate for that room type.
        const applicableRates = rates.filter(rate => packagesById[rate.roomId] && remainingOverallAvailabilityByPackage[rate.roomId] && remainingRoomsByPackageAndSupplier[rate.roomId][rate.supplierCode])
        const selectedRate = applicableRates.length > 0 ?
            applicableRates.reduce((best, rate) => {
              // Prefer a rate that matches `selectedBedbankRoomTypeId`, if possible
              if (best.roomId === selectedBedbankRoomTypeId && rate.roomId !== selectedBedbankRoomTypeId) {
                return best
              } else if (best.roomId !== selectedBedbankRoomTypeId && rate.roomId === selectedBedbankRoomTypeId) {
                return rate
              } else {
                // Otherwise prefer the cheapest option
                return rate.totals.total < best.totals.total ? rate : best
              }
            }) :
          undefined

        // Discount one available room for this package if it is valid
        if (selectedRate) {
          const selection: Selection = { package: packagesById[selectedRate.roomId], rate: selectedRate, selectionType: room.roomId ? 'wasUnavailable' : 'wasBlank' }
          packageSelectionsByRoom[room.id] = selection
          remainingOverallAvailabilityByPackage[selection.package.roomId] -= 1
          remainingRoomsByPackageAndSupplier[selection.package.roomId][selectedRate.supplierCode] -= 1
        }
      }
    })

  return packageSelectionsByRoom
}

export const getActivePackagesByRoom = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState, appState: App.State) => getEffectiveRatesPerRoom(state, appState),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState, appState: App.State) => getTotalAvailabilityByPackageAndSupplier(state, appState),
  (state: OfferPageState, appState: App.State) => getOverallAvailabilityByPackage(state, appState),
  (state: OfferPageState) => state.selectedBedbankRoomId,
  determinePackagesByRoom,
)

export const getCheapestPackagesByRoom = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState, appState: App.State) => getHotelOnlyRatesPerRoom(state, appState),
  (state: OfferPageState, appState: App.State) => getBundleRatesPerRoom(state, appState),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState, appState: App.State) => getTotalAvailabilityByPackageAndSupplier(state, appState),
  (state: OfferPageState, appState: App.State) => getOverallAvailabilityByPackage(state, appState),
  (state: OfferPageState) => state.selectedBedbankRoomId,
  (offer, offerRatesByRoomHotelOnly, offerRatesByRoomWithFlights, rooms = initialRooms, checkIn, checkOut, availabilityByPackageAndSupplier, overallAvailabilityByPackage, selectedBedbankRoomTypeId) => {
    const roomsWithoutSelections = rooms.map(omitKeys(['roomId', 'rateId']))

    return {
      hotelOnly: determinePackagesByRoom(offer, offerRatesByRoomHotelOnly, roomsWithoutSelections, checkIn, checkOut, availabilityByPackageAndSupplier, overallAvailabilityByPackage, selectedBedbankRoomTypeId),
      bundle: determinePackagesByRoom(offer, offerRatesByRoomWithFlights, roomsWithoutSelections, checkIn, checkOut, availabilityByPackageAndSupplier, overallAvailabilityByPackage, selectedBedbankRoomTypeId),
    }
  },
)

/**
 * Like getCheapestPackagesByRoom but preserves the user's current room/rate
 * selections. For rooms with selections it returns the selected rate; for
 * rooms without selections it falls back to the cheapest available rate.
 *
 * Returns separate hotel-only and bundle selections so callers can display
 * both hotel and hotel+flights pricing.
 */
export const getActiveHotelOnlyAndBundlePackagesByRoom = createSelector(
  (state: OfferPageState) => state.bedbankOffer,
  (state: OfferPageState, appState: App.State) => getHotelOnlyRatesPerRoom(state, appState),
  (state: OfferPageState, appState: App.State) => getBundleRatesPerRoom(state, appState),
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState, appState: App.State) => getTotalAvailabilityByPackageAndSupplier(state, appState),
  (state: OfferPageState, appState: App.State) => getOverallAvailabilityByPackage(state, appState),
  (state: OfferPageState) => state.selectedBedbankRoomId,
  (offer, offerRatesByRoomHotelOnly, offerRatesByRoomWithFlights, rooms = initialRooms, checkIn, checkOut, availabilityByPackageAndSupplier, overallAvailabilityByPackage, selectedBedbankRoomTypeId) => {
    return {
      hotelOnly: determinePackagesByRoom(offer, offerRatesByRoomHotelOnly, rooms, checkIn, checkOut, availabilityByPackageAndSupplier, overallAvailabilityByPackage, selectedBedbankRoomTypeId),
      bundle: determinePackagesByRoom(offer, offerRatesByRoomWithFlights, rooms, checkIn, checkOut, availabilityByPackageAndSupplier, overallAvailabilityByPackage, selectedBedbankRoomTypeId),
    }
  },
)

/**
 * Aggregated pricing totals for multi-room individual bedbank bookings.
 * Reads the user's current per-room selections and sums up prices using
 * the canonical `aggregateBedbankMultiRoomPrices` utility.
 *
 * This is the bedbank equivalent of the LE Hotel `getMultiRoomPackageTotals`
 * selector. All components that need multi-room pricing totals (header,
 * footer, sidebar summary) should use this selector.
 */
export const getBedbankMultiRoomPricingTotals = createSelector(
  getActiveHotelOnlyAndBundlePackagesByRoom,
  (state: OfferPageState) => state.rooms,
  (selections, rooms = initialRooms): AggregatedMultiRoomPrices | undefined => {
    const roomsWithRates: Array<{ rate: App.BedbankRate; bundleRate?: App.BedbankRate }> = []

    for (const room of rooms) {
      const hotelOnly = selections.hotelOnly[room.id]
      const bundle = selections.bundle[room.id]
      if (!hotelOnly?.rate) continue
      roomsWithRates.push({
        rate: hotelOnly.rate,
        bundleRate: bundle?.rate,
      })
    }

    if (roomsWithRates.length === 0) return undefined

    return aggregateBedbankMultiRoomPrices(roomsWithRates)
  },
)

export const getRemainingAvailabilityByPackageAndSupplier = createSelector(
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState) => state.checkInDate,
  (state: OfferPageState) => state.checkOutDate,
  (state: OfferPageState, appState: App.State) => getEffectiveRatesPerRoom(state, appState),
  (state: OfferPageState, appState: App.State) => getTotalAvailabilityByPackageAndSupplier(state, appState),
  (state: OfferPageState, appState: App.State) => getActivePackagesByRoom(state, appState),
  (rooms, checkIn, checkOut, offerRatesByRoom, availabilityByPackageAndSupplier, activePackagesByRoom) => {
    // Important: Spread this to a new object since we'll be mutating it
    const remainingRoomsByPackageAndSupplier = structuredClone(availabilityByPackageAndSupplier)

    // Subtract allocated rooms from availability
    for (const room of rooms) {
      const selection = activePackagesByRoom[room.id]
      const roomId = selection?.package?.roomId
      if (roomId && checkIn && checkOut) {
        const rates = offerRatesByRoom[room.id]
        const rate = rates.find(rate => rate.id === selection.rate?.id)
        if (rate && remainingRoomsByPackageAndSupplier[roomId][rate.supplierCode]) {
          remainingRoomsByPackageAndSupplier[roomId][rate.supplierCode] -= 1
        }
      }
    }

    return remainingRoomsByPackageAndSupplier
  },
)

export const getRemainingOverallAvailabilityByPackage = createSelector(
  (state: OfferPageState) => state.rooms,
  (state: OfferPageState, appState: App.State) => getOverallAvailabilityByPackage(state, appState),
  (state: OfferPageState, appState: App.State) => getActivePackagesByRoom(state, appState),
  (rooms, overallAvailabilityByPackage, activePackagesByRoom) => {
    // Important: Spread this to a new object since we'll be mutating it
    const remainingOverallAvailabilityByPackage = { ...overallAvailabilityByPackage }

    // Subtract allocated rooms from availability
    for (const room of rooms) {
      const selection = activePackagesByRoom[room.id]
      const roomId = selection?.package?.roomId
      if (roomId && remainingOverallAvailabilityByPackage[roomId]) {
        remainingOverallAvailabilityByPackage[roomId] -= 1
      }
    }

    return remainingOverallAvailabilityByPackage
  },
)
